import { Plus, Trash2 } from 'lucide-react';
import {
  Input,
  Switch,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { K8sFlavor, K8sGpuModel } from '@/api/k8s';
import { POOL_TYPES, newPool, type PoolDraft } from './cluster-draft';

export function PoolsStep({
  pools,
  clusterUid,
  flavors,
  gpuModels,
  onChange,
}: {
  pools: PoolDraft[];
  /** System cluster id (k8s-c-<slug>) the nodepool name is derived from. */
  clusterUid: string;
  flavors: K8sFlavor[];
  gpuModels: K8sGpuModel[];
  onChange: (pools: PoolDraft[]) => void;
}) {
  const setPool = (i: number, patch: Partial<PoolDraft>) => onChange(pools.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const add = () => onChange([...pools, newPool(pools.length)]);
  const del = (i: number) => onChange(pools.filter((_, j) => j !== i));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {pools.map((pool, i) => {
        // Worker flavors: when GPU is enabled the regular flavor select is replaced
        // by GPU-capable flavors; otherwise match pool type + ≥2 cores (k8s needs ≥2).
        const visible = pool.enableGpu
          ? flavors.filter((f) => f.type === 'gpu' && !f.disabled)
          : flavors.filter((f) => (f.type ?? 'standard') === pool.type && (f.cores ?? 0) >= 2 && !f.disabled);
        return (
          <div key={i} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-lg)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Node pool {i + 1}</strong>
              {pools.length > 1 && <IconButton variant="ghost" size="sm" aria-label="Remove pool" onClick={() => del(i)}><Trash2 size={15} /></IconButton>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label="Pool name">
                {/* Derived + read-only — the nodepool name is auto-generated from the
                    system cluster id (<uid>-<slug>), not free-text, matching @console. */}
                {(p) => <Input {...p} value={`${clusterUid}-${pool.name}`} disabled readOnly />}
              </Field>
              <Field label="Type">
                {(p) => (
                  <Select value={pool.type} onValueChange={(v) => setPool(i, { type: v, flavor: '' })}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>{POOL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <Field label={pool.enableGpu ? 'GPU node flavor' : 'Flavor'} required>
              {(p) => (
                <Select value={pool.flavor} onValueChange={(v) => setPool(i, { flavor: v })}>
                  <SelectTrigger {...p}><SelectValue placeholder={visible.length ? 'Select a flavor' : 'No flavors available'} /></SelectTrigger>
                  <SelectContent>
                    {visible.map((f) => <SelectItem key={f.id ?? f.name} value={f.name}>{f.name}{f.cores ? ` — ${f.cores} vCPU, ${f.memory} GB` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {gpuModels.length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
                <Switch checked={pool.enableGpu} onCheckedChange={(v) => setPool(i, { enableGpu: v, flavor: '' })} /> Enable GPU acceleration
              </label>
            )}
            {gpuModels.length > 0 && pool.enableGpu && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 'var(--vn-space-3)' }}>
                <Field label="GPU model" required>
                  {(p) => (
                    <Select value={pool.gpu.type} onValueChange={(v) => setPool(i, { gpu: { ...pool.gpu, type: v } })}>
                      <SelectTrigger {...p}><SelectValue placeholder="Select GPU" /></SelectTrigger>
                      <SelectContent>{gpuModels.map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.id}{g.vram ? ` (${g.vram} GB)` : ''}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Count">
                  {(p) => (
                    <Select value={String(pool.gpu.count)} onValueChange={(v) => setPool(i, { gpu: { ...pool.gpu, count: Number(v) } })}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>{[1, 2, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100px, 100%), 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label="Min nodes">
                {(p) => <Input {...p} type="number" min={1} max={10} value={pool.min} onChange={(e) => setPool(i, { min: Number(e.target.value) })} />}
              </Field>
              <Field label="Max nodes">
                {(p) => <Input {...p} type="number" min={1} max={10} value={pool.max} onChange={(e) => setPool(i, { max: Number(e.target.value) })} />}
              </Field>
              <Field label="Storage (GiB)">
                {(p) => <Input {...p} type="number" min={30} max={240} value={pool.volumes} onChange={(e) => setPool(i, { volumes: Number(e.target.value) })} />}
              </Field>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
              <Switch checked={pool.autoscaling} onCheckedChange={(v) => setPool(i, { autoscaling: v })} /> Enable autoscaling (min→max)
            </label>
          </div>
        );
      })}
      <div>
        <Button size="sm" variant="secondary" startIcon={<Plus size={15} />} disabled={pools.length >= 3} onClick={add}>Add node pool</Button>
        {pools.length >= 3 && (
          <span style={{ marginLeft: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Maximum of 3 node pools.</span>
        )}
      </div>
    </div>
  );
}
